import React, { useState } from 'react';
import { Alert, Box, Snackbar, Tab, Tabs } from '@mui/material';
import {
  RateReview as FeedbackIcon,
  History as HistoryIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { GiveFeedbackTab } from './components/GiveFeedbackTab';
import { RecentFeedbackTab } from './components/RecentFeedbackTab';
import { AboutMeFeedbackTab } from './components/AboutMeFeedbackTab';
import { useColleagueRatingForm } from './hooks/useColleagueRatingForm';
import { useFeedbackEntries } from './hooks/useFeedbackEntries';
import { useAboutMeEntries } from './hooks/useAboutMeEntries';

const TAB_GIVE = 0;
const TAB_RECENT = 1;
const TAB_ABOUT_ME = 2;

export const ColleagueRatingWidget: React.FC = () => {
  const [tab, setTab] = useState(TAB_GIVE);
  const [recentPage, setRecentPage] = useState(1);
  const [aboutMePage, setAboutMePage] = useState(1);

  const {
    entries,
    totalCount: recentTotalCount,
    isLoading,
    isFetching,
    isError,
  } = useFeedbackEntries(recentPage, tab === TAB_RECENT);

  const {
    entries: aboutMeEntries,
    totalCount: aboutMeTotalCount,
    isLoading: aboutMeLoading,
    isFetching: aboutMeFetching,
    isError: aboutMeError,
  } = useAboutMeEntries(aboutMePage, tab === TAB_ABOUT_ME);

  const form = useColleagueRatingForm({
    onSubmitted: () => {
      setRecentPage(1);
      setAboutMePage(1);
      setTab(TAB_RECENT);
    },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        variant="fullWidth"
      >
        <Tab
          icon={<FeedbackIcon fontSize="small" />}
          iconPosition="start"
          label="Give Feedback"
          sx={{ textTransform: 'none', fontSize: '0.85rem' }}
        />
        <Tab
          icon={<HistoryIcon fontSize="small" />}
          iconPosition="start"
          label="Recent"
          sx={{ textTransform: 'none', fontSize: '0.85rem' }}
        />
        <Tab
          icon={<PersonIcon fontSize="small" />}
          iconPosition="start"
          label="About me"
          sx={{ textTransform: 'none', fontSize: '0.85rem' }}
        />
      </Tabs>

      <Box sx={{ minHeight: 300 }}>
        {tab === TAB_GIVE && (
          <GiveFeedbackTab
            selectedColleague={form.selectedColleague}
            onColleagueChange={form.setSelectedColleague}
            ratings={form.ratings}
            onRatingChange={form.handleRatingChange}
            feedbackText={form.feedbackText}
            onFeedbackTextChange={form.setFeedbackText}
            formError={form.formError}
            submitting={form.submitting}
            isFormValid={form.isFormValid}
            onSubmit={form.handleSubmit}
            onReset={form.handleReset}
          />
        )}

        {tab === TAB_RECENT && (
          <RecentFeedbackTab
            entries={entries}
            page={recentPage}
            totalCount={recentTotalCount}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onPageChange={setRecentPage}
          />
        )}

        {tab === TAB_ABOUT_ME && (
          <AboutMeFeedbackTab
            entries={aboutMeEntries}
            page={aboutMePage}
            totalCount={aboutMeTotalCount}
            isLoading={aboutMeLoading}
            isFetching={aboutMeFetching}
            isError={aboutMeError}
            onPageChange={setAboutMePage}
          />
        )}
      </Box>

      <Snackbar
        open={form.snackOpen}
        autoHideDuration={3500}
        onClose={() => form.setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => form.setSnackOpen(false)} sx={{ width: '100%' }}>
          Anonymous feedback sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};
